import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect,useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { useLazyGetSortingRulesQuery } from '@/services/api/blobService';
import {
  InsuranceCoverageRule,
  SortingRuleCollection,
} from '@/services/types/blob-types';

interface BulkResortDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  matterId: string;
  selectedCount: number;
  isLoading: boolean;
}

export const BulkResortDialog = ({
  isOpen,
  onClose,
  onConfirm,
  matterId,
  selectedCount,
  isLoading,
}: BulkResortDialogProps) => {
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [getSortingRules, { data: sortingRules, isFetching }] =
    useLazyGetSortingRulesQuery();

  // Fetch rules when dialog opens
  const handleOpen = () => {
    if (!rulesLoaded && matterId) {
      getSortingRules({ matterId });
      setRulesLoaded(true);
    }
  };

  // Reset state when dialog closes
  const handleClose = () => {
    setRulesLoaded(false);
    onClose();
  };

  // Trigger fetch when dialog becomes visible
  useEffect(() => {
    if (isOpen && !rulesLoaded && matterId) {
      getSortingRules({ matterId });
      setRulesLoaded(true);
    }
  }, [isOpen, rulesLoaded, matterId, getSortingRules]);

  return (
    <Dialog open={isOpen} onClose={handleClose} size="lg">
      <DialogTitle>Re-Sort {selectedCount} File(s)?</DialogTitle>
      <DialogDescription>
        AI will analyze each file and sort it into the most appropriate folder
        based on the rules below.
      </DialogDescription>

      <DialogBody>
        {isFetching ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading sorting rules...
            </span>
          </div>
        ) : sortingRules ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex items-start gap-2">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {sortingRules.summary}
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {/* Collection Rules */}
              <AccordionItem value="collections">
                <AccordionTrigger className="text-sm font-medium">
                  Folder Categories ({sortingRules.static_collections.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {sortingRules.static_collections.map(
                      (collection: SortingRuleCollection) => (
                        <div
                          key={collection.name}
                          className="border rounded-md p-2"
                        >
                          <div className="font-medium text-sm">
                            {collection.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {collection.summary}
                          </div>
                          {collection.include.length > 0 && (
                            <div className="mt-1 text-xs">
                              <span className="text-green-600">Includes:</span>{' '}
                              {collection.include.slice(0, 3).join(', ')}
                              {collection.include.length > 3 && '...'}
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Approved Insurance Coverage */}
              {sortingRules.approved_coverages.length > 0 && (
                <AccordionItem value="coverage">
                  <AccordionTrigger className="text-sm font-medium">
                    Approved Insurance Policies (
                    {sortingRules.approved_coverages.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {sortingRules.approved_coverages.map(
                        (coverage: InsuranceCoverageRule, index: number) => (
                          <div
                            key={index}
                            className="border rounded-md p-2 flex items-center gap-2"
                          >
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                              {coverage.category}
                            </span>
                            <span className="text-sm">
                              {coverage.carrier_name || 'Unknown Carrier'}
                            </span>
                            {coverage.policy_identifiers.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                Policy: {coverage.policy_identifiers[0]}
                              </span>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Naming Convention */}
              <AccordionItem value="naming">
                <AccordionTrigger className="text-sm font-medium">
                  Naming Convention
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Pattern:</span>{' '}
                      <code className="bg-muted px-1 rounded">
                        {sortingRules.naming_pattern}
                      </code>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Examples:
                      <ul className="list-disc ml-4 mt-1">
                        {sortingRules.naming_examples
                          .slice(0, 3)
                          .flatMap((ne) => ne.examples.slice(0, 2))
                          .map((ex, i) => (
                            <li key={i}>{ex}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4">
            Unable to load sorting rules.
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button plain onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={onConfirm}
          disabled={isLoading || isFetching}
        >
          {isLoading ? 'Re-Sorting...' : 'Re-Sort Files'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkResortDialog;
